import React, { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import { addBudgetCostItem, deleteBudgetCostItem, updateBudgetCostItem } from '../../../../services/budgetAPI';
import { buildFormStyles, money, Pill, TreasuryDrawer } from '../treasury/treasuryUi';
import { flattenBudgetAccounts } from './BudgetAccountsPanel';
import { PartyFields, PartyHint } from './budgetPartyFields';

const BUSINESS_UNITS = ['Pinturas', 'Aditivos', 'Servicio', 'Backoffice'];
const UNIT_TONE = { Pinturas: 'info', Aditivos: 'purple', Servicio: 'success', Backoffice: 'warning' };

const monthKey = (year, month) => `${Number(year)}-${String(Number(month) || 1).padStart(2, '0')}`;

const fallbackAxis = (year) =>
  MONTH_LABELS.map((label, index) => ({
    year: Number(year),
    month: index + 1,
    label: `${label} ${year}`,
    short_label: label,
  }));

const parseNumber = (value) => Number(String(value ?? '').replace(',', '.')) || 0;

const emptyAmounts = (count) => Array.from({ length: Math.max(count, 1) }, () => '');

const BudgetPunctualCostPanel = ({
  ui,
  isDarkMode = false,
  budget = null,
  budgetAccounts = [],
  canEdit = false,
  onChanged,
}) => {
  const styles = buildFormStyles(ui);
  const [search, setSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    business_unit: 'Pinturas',
    budget_account_id: '',
    notes: '',
    supplier: '',
    payment_term: 'contado',
    amounts: [],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const budgetYear = Number(budget?.year) || new Date().getFullYear();
  const monthAxis = useMemo(
    () => (Array.isArray(budget?.month_axis) && budget.month_axis.length ? budget.month_axis : fallbackAxis(budgetYear)),
    [budget, budgetYear]
  );
  const costAccounts = useMemo(
    () => flattenBudgetAccounts(budgetAccounts).filter((row) => row.nature === 'costo' || row.nature === 'gasto'),
    [budgetAccounts]
  );

  const monthRows = useMemo(
    () =>
      monthAxis.map((col, index) => ({
        ...col,
        amount: parseNumber(form.amounts[index]),
      })),
    [monthAxis, form.amounts]
  );
  const totalAmount = monthRows.reduce((sum, row) => sum + row.amount, 0);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (budget?.cost_items || [])
      .filter((item) => item.schedule_kind === 'punctual')
      .filter((item) => {
        if (unitFilter !== 'all' && item.business_unit !== unitFilter) return false;
        if (!q) return true;
        return `${item.name} ${item.account_label} ${item.account_code} ${item.business_unit} ${item.supplier}`.toLowerCase().includes(q);
      });
  }, [budget, search, unitFilter]);

  const amountsFromItem = (item) =>
    monthAxis.map((col, index) => {
      const key = monthKey(col.year, col.month);
      const scheduled = item?.amount_schedule?.[key];
      if (scheduled != null && scheduled !== '') return String(scheduled);
      const listValue = item?.months?.[index];
      return listValue ? String(listValue) : '';
    });

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      business_unit: 'Pinturas',
      budget_account_id: '',
      notes: '',
      supplier: '',
      payment_term: 'contado',
      amounts: emptyAmounts(monthAxis.length),
    });
    setError('');
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      business_unit: item.business_unit || 'Pinturas',
      budget_account_id: String(item.budget_account_id || item.allocations?.[0]?.budget_account_id || ''),
      notes: item.notes || '',
      supplier: item.supplier || '',
      payment_term: item.payment_term || 'contado',
      amounts: amountsFromItem(item),
    });
    setError('');
    setShowForm(true);
  };

  const setAmountAt = (index, value) => {
    setForm((prev) => {
      const next = [...(prev.amounts || [])];
      while (next.length < monthAxis.length) next.push('');
      next[index] = value;
      return { ...prev, amounts: next };
    });
  };

  const payloadFromForm = () => {
    const amount_months = {};
    monthAxis.forEach((col, index) => {
      const amount = parseNumber(form.amounts[index]);
      if (Math.abs(amount) > 0.004) amount_months[monthKey(col.year, col.month)] = amount;
    });
    return {
      name: form.name,
      amount: totalAmount,
      schedule_kind: 'punctual',
      amount_months,
      recurrence: 'Mensual',
      business_unit: form.business_unit,
      budget_account_id: form.budget_account_id ? Number(form.budget_account_id) : null,
      notes: form.notes,
      supplier: form.supplier,
      payment_term: form.payment_term || 'contado',
      splits: [
        {
          budget_account_id: form.budget_account_id ? Number(form.budget_account_id) : null,
          share_kind: 'percent',
          percent: 100,
        },
      ],
    };
  };

  const handleSave = async () => {
    if (!budget?.id) return;
    setBusy(true);
    setError('');
    try {
      const payload = payloadFromForm();
      const next = editing
        ? await updateBudgetCostItem(budget.id, editing.id, payload)
        : await addBudgetCostItem(budget.id, payload);
      setShowForm(false);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo guardar el costo puntual.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar ${item.name}?`)) return;
    setBusy(true);
    setError('');
    try {
      const next = await deleteBudgetCostItem(budget.id, item.id);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el costo puntual.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>Costos puntuales</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
          Montos distintos cada mes, independientes del nivel de ventas. Útiles para campañas, mantenciones u otros gastos no recurrentes.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar ítem o cuenta…"
            style={{ ...styles.input, width: 260 }}
          />
          <select value={unitFilter} onChange={(event) => setUnitFilter(event.target.value)} style={{ ...styles.input, width: 170 }}>
            <option value="all">Todas las unidades</option>
            {BUSINESS_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </div>
        {canEdit ? (
          <button type="button" style={styles.primaryBtn} onClick={openCreate}>
            <Plus size={14} /> Agregar costo puntual
          </button>
        ) : null}
      </div>
      {error && !showForm ? <p style={{ margin: '0 0 10px', color: ui.danger, fontSize: 12 }}>{error}</p> : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
          <thead>
            <tr>
              {['Ítem', 'Unidad', 'Cuenta', 'Monto', ''].map((label) => (
                <th
                  key={label || 'actions'}
                  style={{
                    textAlign: label === 'Monto' ? 'right' : 'left',
                    padding: 8,
                    borderBottom: `1px solid ${ui.border}`,
                    fontSize: 11,
                    color: ui.muted,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.id}>
                <td style={{ padding: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: ui.text }}>{item.name}</div>
                  <PartyHint item={item} ui={ui} />
                  {item.notes ? <div style={{ fontSize: 11, color: ui.muted }}>{item.notes}</div> : null}
                </td>
                <td style={{ padding: 8 }}>
                  <Pill label={item.business_unit || '—'} tone={UNIT_TONE[item.business_unit] || 'info'} isDarkMode={isDarkMode} />
                </td>
                <td style={{ padding: 8, fontSize: 12, color: ui.text, whiteSpace: 'nowrap' }}>
                  {item.account_label || item.account_code || '—'}
                </td>
                <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>{money(item.budget_total)}</td>
                <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canEdit ? (
                    <>
                      <button type="button" style={styles.ghostBtn} onClick={() => openEdit(item)}>
                        <Pencil size={13} />
                      </button>
                      <button type="button" style={styles.ghostBtn} onClick={() => handleDelete(item)} disabled={busy}>
                        <Trash2 size={13} />
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={5} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                  Agrega gastos con un monto distinto cada mes, sin ligarlos a las ventas.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showForm ? (
        <TreasuryDrawer
          ui={ui}
          title={editing ? 'Editar costo puntual' : 'Nuevo costo puntual'}
          subtitle="Indica el monto de cada mes. No depende de las unidades ni de las ventas proyectadas."
          error={error}
          saving={busy}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        >
          <label style={styles.label}>
            Nombre
            <input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} style={styles.input} />
          </label>
          <label style={styles.label}>
            Unidad de negocio
            <select value={form.business_unit} onChange={(event) => setForm((prev) => ({ ...prev, business_unit: event.target.value }))} style={styles.input}>
              {BUSINESS_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </label>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: ui.text }}>Monto por mes</span>
              <span style={{ fontSize: 12, color: ui.muted }}>{money(totalAmount)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))', gap: 8 }}>
              {monthRows.map((row, index) => (
                <label key={`${row.year}-${row.month}`} style={{ ...styles.label, margin: 0 }}>
                  {row.short_label || row.label}
                  <input
                    value={form.amounts[index] ?? ''}
                    onChange={(event) => setAmountAt(index, event.target.value)}
                    style={styles.input}
                    inputMode="decimal"
                  />
                </label>
              ))}
            </div>
          </div>
          <label style={styles.label}>
            Clasificación
            <select
              value={form.budget_account_id}
              onChange={(event) => setForm((prev) => ({ ...prev, budget_account_id: event.target.value }))}
              style={styles.input}
            >
              <option value="">Selecciona una cuenta de costo o gasto</option>
              {costAccounts.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.optionLabel}
                </option>
              ))}
            </select>
            {!costAccounts.length ? (
              <span style={{ fontSize: 11, color: ui.muted }}>Crea cuentas de costo o gasto en la píldora Cuentas.</span>
            ) : null}
          </label>
          <PartyFields form={form} setForm={setForm} styles={styles} />
          <label style={styles.label}>
            Notas
            <input value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} style={styles.input} />
          </label>
        </TreasuryDrawer>
      ) : null}
    </div>
  );
};

export default BudgetPunctualCostPanel;
